import { useCallback, useState } from 'react';
import { motion, useAnimate } from 'framer-motion';
import { SmartTooltip } from '~/commonComponents/SmartTooltip/SmartTooltip';
import HeartIcon from '~/images/icons/Heart';
import { Colors } from '~/themes/colors';

interface IFavoriteHeartButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isFavorite?: boolean;
  isPendingRemoval?: boolean;
}

// Keyframes matching mobile: rise+tilt-left → top-center → fall+tilt-right → rest
const heartBounceKeyframes = {
  y: [0, -6, -10, -6, 0],
  rotate: [0, -18, 0, 18, 0],
};
const heartBounceDuration = 0.38; // 100+80+100+100 = 380ms

export const FavoriteHeartButton = ({
  onClick,
  disabled = false,
  isFavorite = false,
  isPendingRemoval = false,
}: IFavoriteHeartButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [heartFilled, setHeartFilled] = useState(false);
  const [scope, animate] = useAnimate();

  const getHeartColor = () => {
    if (isFavorite || heartFilled) {
      return isHovered ? Colors.favorites_350 : Colors.favorites_400;
    }
    return Colors.favorites_400;
  };

  const getOutline = () => {
    if (isFavorite) return false;
    if (heartFilled) return false;
    return !isHovered;
  };

  const handleClick = useCallback(async () => {
    if (disabled) return;

    if (isFavorite) {
      onClick();
    } else {
      setHeartFilled(true);
      await animate(
        '[data-testid="heart-bounce-wrapper"]',
        heartBounceKeyframes,
        { duration: heartBounceDuration },
      );
      onClick();
    }
  }, [disabled, isFavorite, animate, onClick]);

  const tooltipContent = isFavorite ? 'Remove' : 'Add to Favorites';

  return (
    <div
      style={{
        position: 'absolute',
        top: 10,
        right: 12,
      }}
    >
      <SmartTooltip
        content={tooltipContent}
        position="bottom"
        alignment="center"
        trigger="hover"
        className="smart-tooltip"
        disabled={disabled}
      >
        <button
          type="button"
          data-testid="favorite-heart-button"
          disabled={disabled}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            all: 'unset',
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.4 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            filter: isHovered ? 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))' : 'none',
            transition: 'filter 0.2s',
          }}
          ref={scope}
        >
          <motion.div data-testid="heart-bounce-wrapper">
            {isFavorite ? (
              <div style={{ position: 'relative' }}>
                <HeartIcon color={Colors.favorites_400} outline width={25} height={25} />
                <motion.div
                  data-testid="heart-fade-overlay"
                  animate={{ opacity: isPendingRemoval ? 0 : 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ position: 'absolute', top: 0, left: 0 }}
                >
                  <HeartIcon color={getHeartColor()} outline={false} width={25} height={25} />
                </motion.div>
              </div>
            ) : (
              <HeartIcon color={getHeartColor()} outline={getOutline()} width={25} height={25} />
            )}
          </motion.div>
        </button>
      </SmartTooltip>
    </div>
  );
};
